import {Component, computed, effect, inject, viewChild} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {PageGridComponent} from '../../../../shared/components/page-grid/page-grid.component';
import {AdxErpPanelFilterService} from '../../service/adx-erp-panel-filter.service';
import {AdxErpReportGridService} from '../../service/adx-erp-report-grid.service';
import {AdxErpReportActionService} from '../../service/adx-erp-report-action.service';
import {GridEventOutput, GridModel} from '../../../../shared/model/page-grid';
import {IAdxErpReportItem} from '../../interfaces/adx-erp-panel.interface';
import {DatePipe} from '@angular/common';
import {environment} from '../../../../../environments/environment';
import {AdxErpPayloadDialogComponent} from '../adx-erp-payload-dialog/adx-erp-payload-dialog.component';
import {AdxInvoiceDetailsModalComponent} from '../../../../adx-invoice/components/adx-invoice-details-modal/adx-invoice-details-modal.component';
import {
  AdxErpManualUpdateDialogComponent,
} from '../adx-erp-manual-update-dialog/adx-erp-manual-update-dialog.component';

@Component({
  selector: 'app-adx-erp-report-list',
  templateUrl: './adx-erp-report-list.component.html',
  standalone: false,
})
export class AdxErpReportListComponent {
  private readonly filterService = inject(AdxErpPanelFilterService);
  private readonly gridService = inject(AdxErpReportGridService);
  private readonly actionService = inject(AdxErpReportActionService);
  private readonly datePipe = inject(DatePipe);
  private readonly dialog = inject(MatDialog);

  protected readonly gridRef = viewChild.required(PageGridComponent);

  protected readonly gridConfig = computed<GridModel<IAdxErpReportItem>>(() => ({
    gridDataTestId: 'adx-erp-report-list',
    gridTitle: 'ADX → ERP Invoice Report',
    defaultPageSize: 25,
    replayButton: true,
    // Manual ERP close-out writes directly to invoice/response state, so — same as QA Playground —
    // it's gated off in production and only available in non-prod environments.
    showActionColumn: environment.enablePlayground,
    actionColumnHeader: 'Manual Update',
    columns: [
      {
        columnProperty: 'fmsAdxInvoiceId',
        columnHeader: 'ADX Invoice ID',
        sort: true,
        clickable: true,
        linkEventFunction: (item: IAdxErpReportItem) => `AdxInvoices/${item.fmsAdxInvoiceId}`,
      },
      {
        columnProperty: 'adxReference',
        columnHeader: 'ADX Reference',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'invoiceGroup',
        columnHeader: 'Invoice Group',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'travelTypeNames',
        columnHeader: 'Travel Type',
        sort: false,
        textLeft: true,
        extraWidth: true,
      },
      {
        columnProperty: 'profileName',
        columnHeader: 'Client Name',
        sort: true,
        textLeft: true,
        extraWidth: true,
      },
      {
        columnProperty: 'erpOrderNumber',
        columnHeader: 'ERP Sales Order',
        sort: true,
        textLeft: true,
      },
      {
        columnProperty: 'dateCreated',
        columnHeader: 'ADX Invoice Created',
        sort: true,
        processData: (item) => this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '',
      },
      {
        columnProperty: 'erpMessageTimestamp',
        columnHeader: 'Sent to ERP',
        sort: true,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
      {
        columnProperty: 'erpResponseDate',
        columnHeader: 'ERP Response Date',
        sort: true,
        processData: (item) => item ? (this.datePipe.transform(item, 'M/dd/yyyy HH:mm') ?? '') : '',
      },
      {
        columnProperty: 'erpProcessingError',
        columnHeader: 'Processing Error',
        sort: false,
        extraWidth: true,
        wrapContent: true,
        textLeft: true,
      },
      {
        columnProperty: 'errorDescription',
        columnHeader: 'ERP Error',
        sort: false,
        processData: (item) => item ? 'View Error' : '',
        linkStyle: () => ({'color': '#d32f2f', 'text-decoration': 'underline', 'cursor': 'pointer'}),
        clickableWithItem: (item) => !!item.errorDescription,
      },
      {
        columnProperty: 'hasPayload',
        columnHeader: 'Payload',
        sort: false,
        processData: (item: any) => item === true ? 'View Payload' : '',
        linkStyle: () => ({ 'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer' }),
        clickableWithItem: (item) => item.hasPayload === true,
      },
      {
        columnProperty: 'erpRetryCount',
        columnHeader: 'Retries',
        sort: false,
      },
      {
        columnProperty: 'erpIsFailed',
        columnHeader: 'Retry',
        sort: false,
        processData: (item: any) => item === true ? 'Reset & Retry' : '',
        linkStyle: () => ({'color': '#0078d4', 'text-decoration': 'underline', 'cursor': 'pointer'}),
        clickableWithItem: (item) => item.erpIsFailed === true,
      },
    ],
    gridGetService: this.gridService,
  }));

  constructor() {
    effect(() => {
      const filters = this.filterService.filtersValue();
      if (!filters.ReloadGrid) return;
      this.gridRef().getItems();
    });
  }

  protected handleColumnClick(event: GridEventOutput<IAdxErpReportItem>): void {
    if (event.column === 'hasPayload' && event.item.hasPayload) {
      this.actionService.getPayload(event.item.fmsAdxInvoiceId).subscribe(payload => {
        this.dialog.open(AdxErpPayloadDialogComponent, {
          width: '800px',
          maxWidth: '95vw',
          data: { title: 'Invoice Payload', content: payload },
        });
      });
    }
    if (event.column === 'errorDescription' && event.item.errorDescription) {
      this.dialog.open(AdxErpPayloadDialogComponent, {
        width: '800px',
        maxWidth: '95vw',
        data: {title: 'ERP Error', content: event.item.errorDescription},
      });
    }
    if (event.column === 'erpIsFailed' && event.item.erpIsFailed) {
      this.actionService.resetErp(event.item.fmsAdxInvoiceId).subscribe(() => {
        this.gridRef().getItems();
      });
    }
    if (event.column === 'fmsAdxInvoiceId' && event.item.fmsAdxInvoiceId) {
      this.openInvoiceDialog(event.item.fmsAdxInvoiceId);
    }
  }

  private openInvoiceDialog(fmsAdxInvoiceId: number): void {
    this.dialog.open(AdxInvoiceDetailsModalComponent, {
      width: '55%',
      data: { adxInvoiceId: fmsAdxInvoiceId, title: 'Invoice Information' }
    });
  }

  protected openManualUpdateDialog(item: IAdxErpReportItem): void {
    if (!environment.enablePlayground) return;

    this.dialog.open(AdxErpManualUpdateDialogComponent, {
      width: '520px',
      maxWidth: '95vw',
      data: {
        adxInvoiceId: item.fmsAdxInvoiceId,
        adxReference: item.adxReference,
        alreadySynced: !!item.erpMessageId,
        existingResponse: item.adxInvoiceErpResponseId ? {
          erpResponseId: item.adxInvoiceErpResponseId,
          erpMessageId: item.erpMessageId,
          erpOrderNumber: item.erpOrderNumber,
          errorDescription: item.errorDescription,
        } : null,
      },
    }).afterClosed().subscribe((result?: {updated: boolean}) => {
      if (result?.updated) {
        this.gridRef().getItems();
      }
    });
  }
}
